import type {ReactElement} from 'react';
import {Button, IconPlacement, Size, Type} from '@Components/button';
// eslint-disable-next-line import/no-extraneous-dependencies
import useEyeDropper from 'use-eye-dropper';
import {Icon} from "@Components/icon-v2";
import styles from "./eyedropper-color-picker.module.scss";
import {IconShape, IconSize, IconType} from "@Components/icon-v2/icon.types";

interface EyedropperColorPickerProps {
  onColorPicked: (color: string) => void;
  onPickStart?: VoidFunction;
  onPickEnd?: VoidFunction;
  customClasses?: string;
  ctaType?: EyeDropperCTAType;
  /** Size of the ICON cta; COMPACT is the design system's 32×32 square icon button (16px icon), matching XSMALL input fields. */
  iconCtaSize?: EyeDropperIconCTASize;
}

export enum EyeDropperCTAType {
  ICON,
  BUTTON,
}

export enum EyeDropperIconCTASize {
  DEFAULT = 'default',
  COMPACT = 'compact',
}

export function EyedropperColorPicker({ctaType = EyeDropperCTAType.BUTTON, iconCtaSize = EyeDropperIconCTASize.DEFAULT, ...props}: EyedropperColorPickerProps): ReactElement | null {
  const {open, isSupported} = useEyeDropper();

  const invokeOnPickStart = (): void => {
    if (props.onPickStart) {
      props.onPickStart();
    }
  };

  const invokeOnPickEnd = (): void => {
    if (props.onPickEnd) {
      props.onPickEnd();
    }
  };

  const pickColor = (): void => {
    const openPicker = async (): Promise<void> => {
      try {
        invokeOnPickStart();
        const pickedColor = await open();
        props.onColorPicked(pickedColor.sRGBHex);
        invokeOnPickEnd();
      } catch (e) {
        console.log(e);
      }
    };

    void openPicker();
  };

  if (!isSupported()) {
    return null;
  }

  const isCompactIconCTA = iconCtaSize === EyeDropperIconCTASize.COMPACT;

  const getEyeDropperCTA = (): ReactElement => {
    if (ctaType === EyeDropperCTAType.ICON) {
      return (
        <Icon
          onClick={pickColor}
          className={`${isCompactIconCTA ? styles.compactPickerIcon : styles.pickerIcon} spacing-m-l-2`}
          icon="icon-colorpicker"
          size={isCompactIconCTA ? IconSize.SIZE_ICON_16 : IconSize.SIZE_ICON_20}
          shape={IconShape.SQUARE}
          type={IconType.SECONDARY}
        />
      )
    }

    return (
      <Button
        customClasses={props.customClasses}
        type={Type.SECONDARY}
        size={Size.SMALL}
        text={window.i18next.t('pmwjs_picker')}
        iconPlacement={IconPlacement.LEFT}
        icon="icon-colorpicker"
        onClick={pickColor}
      />
    )
  };

  return getEyeDropperCTA();
}
